/**
 * The password input, everywhere a password is typed.
 *
 * IT EXISTS FOR THE TOGGLE, NOT FOR THE LABEL. On four screens a password is
 * typed into a field whose value can never be read back, twice on three of them,
 * and "my password does not work" is usually a keyboard layout nobody could see.
 * A show/hide button written four times is a button that behaves four ways — one
 * of them will end up defaulting to visible, or submitting the form, or losing
 * the caret. So it is written once: hidden by default, and a real
 * `<button type="button">` so that Enter in the field still submits.
 *
 * THE RULE HINT STATES THE ONE RULE THIS BUNDLE CAN CHECK, AND CLAIMS NOTHING
 * ELSE. The panel also rejects a password containing the email's local part and
 * anything on a common-password blocklist. Neither is knowable here — the
 * blocklist is server-side by design, and the address is not always on screen —
 * so the hint is the length, live, and the server's `weak_password` message
 * names whatever else failed. A tick-list that looked complete would be
 * confidently wrong in exactly the two cases that matter.
 *
 * NO STRENGTH METER, for the same reason. A bar that reads "strong" for a
 * twelve-character password on the blocklist is a measurement of nothing,
 * rendered as reassurance.
 *
 * `autoComplete` IS REQUIRED, not optional with a default. It is the only thing
 * a password manager reads, and the difference between `current-password` and
 * `new-password` decides whether a manager offers to fill or offers to save.
 * Getting it wrong is silent, so there is no default to get wrong.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'

/** The panel's minimum. There is no maximum worth showing: it is 200. */
export const MIN_PASSWORD_LENGTH = 12

/**
 * Why a new-password pair cannot be submitted yet, or null when it can.
 *
 * Three screens ask the same two questions in the same order, and three copies
 * would eventually disagree about which to complain about first — the annoying
 * version being the one that says "they do not match" while the first box is
 * still four characters short.
 */
export function passwordPairProblem(password: string, confirm: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `The new password needs at least ${MIN_PASSWORD_LENGTH} characters.`
  }
  if (password !== confirm) return 'The two passwords do not match.'
  return null
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  showRule = false,
  hint,
  disabled,
  autoFocus,
  labelEnd,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  /** `current-password` to recall one, `new-password` to choose one. */
  autoComplete: 'current-password' | 'new-password'
  /** The live length rule. On for a password being chosen, off for one being recalled. */
  showRule?: boolean
  hint?: ReactNode
  disabled?: boolean
  autoFocus?: boolean
  /**
   * Rendered at the far end of the label row — the "Forgot your password?" slot.
   * A prop rather than a sibling because the link belongs on the label's
   * baseline, and only this component knows where that row is.
   */
  labelEnd?: ReactNode
}) {
  const [visible, setVisible] = useState(false)
  const longEnough = value.length >= MIN_PASSWORD_LENGTH
  const remaining = MIN_PASSWORD_LENGTH - value.length

  return (
    <div className="field gap-1.5">
      {labelEnd ? (
        <div className="flex items-center">
          <Label htmlFor={id}>{label}</Label>
          <span className="ml-auto text-sm">{labelEnd}</span>
        </div>
      ) : (
        <Label htmlFor={id}>{label}</Label>
      )}
      <InputGroup>
        <InputGroupInput
          id={id}
          // Not `type={visible ? …}` on a plain Input: the group owns the border
          // so the toggle sits inside it rather than beside it, which is the only
          // arrangement that survives the field being 100% wide on a phone.
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          spellCheck={false}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            // The accessible name says what pressing it will DO, not what the
            // field is doing now. A screen reader user hitting "Password shown"
            // has to work out whether that is a state or an instruction.
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
            disabled={disabled}
            onClick={() => setVisible((v) => !v)}
          >
            {visible ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>

      {showRule && (
        <p
          className={cn(
            'flex items-center gap-1 text-xs',
            longEnough ? 'text-ev-good' : 'text-muted-foreground',
          )}
        >
          {longEnough && <CheckIcon className="size-3 shrink-0" aria-hidden />}
          At least {MIN_PASSWORD_LENGTH} characters
          {/* The countdown only appears once somebody has started, so an empty
              field reads as a rule rather than as a complaint. */}
          {value.length > 0 && !longEnough
            && ` — ${remaining} more to go`}
        </p>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}
